import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { concat } from '@ember/helper';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Button from 'tio-reimagined/components/button';
import { or } from 'tio-ui/utilities';
import {
  PhTrophy,
  PhStar,
  PhRocket,
  PhTarget,
  PhCrown,
  PhMedal,
  PhCheckCircle,
  PhShare,
  PhDownload,
  PhFlag,
} from 'ember-phosphor-icons';
import type { PslfAchievement } from 'tio-employee/services/career-pathways-api';

interface MilestoneBadgeSignature {
  Args: {
    achievement: PslfAchievement;
    onShare?: (achievement: PslfAchievement) => void;
    onDownload?: (achievement: PslfAchievement) => void;
    compact?: boolean;
  };
  Element: HTMLDivElement;
}

// Map milestone types to icons. The per-tier gradient is a CSS recipe
// (`.tio-milestone-tier--<type>`) keyed off the milestone type, so the
// achievement-tier color encoding lives in the stylesheet, not inline.
function getMilestoneIcon(type: PslfAchievement['milestoneType']) {
  const icons: Record<string, typeof PhTrophy> = {
    started: PhFlag,
    first_year: PhStar,
    two_years: PhTarget,
    four_years: PhMedal,
    halfway: PhRocket,
    six_years: PhTrophy,
    eight_years: PhTrophy,
    home_stretch: PhRocket,
    final_countdown: PhTarget,
    forgiveness: PhCrown,
  };
  return icons[type] || icons.started;
}

export default class PslfMilestoneBadge extends Component<MilestoneBadgeSignature> {
  @tracked showConfetti = false;

  get icon() {
    return getMilestoneIcon(this.args.achievement.milestoneType);
  }

  get tierClass() {
    return `tio-milestone-tier--${this.args.achievement.milestoneType}`;
  }

  get isUnlocked() {
    return this.args.achievement.achievedAt !== null;
  }

  get formattedDate() {
    if (!this.args.achievement.achievedAt) return null;
    return new Date(this.args.achievement.achievedAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  get isForgiveness() {
    return this.args.achievement.milestoneType === 'forgiveness';
  }

  handleShare = () => {
    this.args.onShare?.(this.args.achievement);
  };

  handleDownload = () => {
    this.args.onDownload?.(this.args.achievement);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    {{#if @compact}}
      {{! Compact badge view for grids }}
      <div class="relative group cursor-pointer" title="{{@achievement.badgeName}}" ...attributes>
        <div
          class="w-16 h-16 rounded-full tio-row-center
            {{if this.isUnlocked (concat 'tio-milestone-tier ' this.tierClass) 'bg-gray-700/50'}}
            {{unless this.isUnlocked 'opacity-40'}}
            shadow-lg transform transition-transform group-hover:scale-110"
        >
          {{#if this.isUnlocked}}
            <span class="text-2xl">{{@achievement.badgeEmoji}}</span>
          {{else}}
            <this.icon @size={{28}} class="tio-milestone-locked-glyph" />
          {{/if}}
        </div>
        {{#unless this.isUnlocked}}
          <div class="absolute inset-0 tio-row-center">
            <div class="w-16 h-16 rounded-full border-2 border-dashed tio-border-strong"></div>
          </div>
        {{/unless}}
        {{#if this.isForgiveness}}
          <div class="absolute -top-1 -right-1">
            <span class="text-lg">👑</span>
          </div>
        {{/if}}
      </div>
    {{else}}
      {{! Full achievement card }}
      <div class="tio-milestone-card" ...attributes>
        {{! Header with badge }}
        <div
          class="relative h-32 tio-row-center
            {{if this.isUnlocked (concat 'tio-milestone-tier ' this.tierClass) 'bg-gray-800'}}"
        >
          {{! Decorative elements }}
          {{#if this.isUnlocked}}
            <div class="absolute inset-0 overflow-hidden">
              <div class="absolute top-4 left-4 w-12 h-12 bg-white/10 rounded-full blur-xl"></div>
              <div
                class="absolute bottom-4 right-8 w-16 h-16 bg-white/10 rounded-full blur-xl"
              ></div>
            </div>
          {{/if}}

          {{! Badge icon }}
          <div
            class="relative z-10 w-20 h-20 rounded-full tio-row-center
              {{if this.isUnlocked 'bg-white/20 backdrop-blur-sm' 'bg-gray-700/50'}}
              border-4
              {{if this.isUnlocked 'border-white/30' 'tio-border-strong'}}"
          >
            {{#if this.isUnlocked}}
              <span class="text-4xl">{{@achievement.badgeEmoji}}</span>
            {{else}}
              <this.icon @size={{40}} class="tio-milestone-locked-glyph" />
            {{/if}}
          </div>

          {{! Payment count badge }}
          {{#if this.isUnlocked}}
            <div
              class="absolute top-4 right-4 px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wide tio-fg-inverse bg-white/20"
            >
              {{@achievement.payments}}
              payments
            </div>
          {{/if}}

          {{! Locked overlay }}
          {{#unless this.isUnlocked}}
            <div class="absolute inset-0 tio-row-center bg-black/40">
              <span class="text-4xl opacity-60">🔒</span>
            </div>
          {{/unless}}
        </div>

        {{! Content }}
        <div class="p-5">
          <div class="text-center mb-4">
            <Heading
              @level={{3}}
              class="text-white font-bold {{unless this.isUnlocked 'opacity-60'}}"
            >
              {{@achievement.badgeName}}
            </Heading>
            <Text @size="sm" class="tio-fg-disabled mt-1 {{unless this.isUnlocked 'opacity-60'}}">
              {{@achievement.message}}
            </Text>
          </div>

          {{#if this.isUnlocked}}
            {{! Unlocked state }}
            <div class="tio-milestone-unlocked tio-row-center gap-2 text-sm mb-4">
              <PhCheckCircle @size={{16}} />
              <span>Unlocked {{this.formattedDate}}</span>
            </div>

            {{! Stats if available }}
            {{#if @achievement.statsAtMilestone}}
              <div class="bg-black/20 rounded-lg p-3 mb-4">
                <div class="grid grid-cols-2 gap-3 text-center">
                  <div>
                    <Text @size="lg" @weight="bold" class="tio-fg-inverse">
                      {{@achievement.statsAtMilestone.percentComplete}}%
                    </Text>
                    <Text @size="xs" class="tio-fg-muted">Complete</Text>
                  </div>
                  <div>
                    <Text @size="lg" @weight="bold" class="tio-fg-inverse">
                      ${{@achievement.statsAtMilestone.forgivenessEarned}}
                    </Text>
                    <Text @size="xs" class="tio-fg-muted">Earned</Text>
                  </div>
                </div>
              </div>
            {{/if}}

            {{! Share/Download actions }}
            {{#if (or @onShare @onDownload)}}
              <div class="tio-flex-tight">
                {{#if @onShare}}
                  <Button
                    @intent="primary"
                    @appearance="minimal"
                    class="flex-1 justify-center"
                    {{on "click" this.handleShare}}
                  >
                    <PhShare @size={{16}} />
                    <span>Share</span>
                  </Button>
                {{/if}}
                {{#if @onDownload}}
                  <Button
                    @intent="primary"
                    @appearance="minimal"
                    class="flex-1 justify-center"
                    {{on "click" this.handleDownload}}
                  >
                    <PhDownload @size={{16}} />
                    <span>Certificate</span>
                  </Button>
                {{/if}}
              </div>
            {{/if}}
          {{else}}
            {{! Locked state - show target payments }}
            <div class="text-center">
              <Text @size="sm" class="tio-fg-muted">
                Reach
                {{@achievement.payments}}
                payments to unlock
              </Text>
            </div>
          {{/if}}
        </div>
      </div>
    {{/if}}
  </template>
}
